import React, { useState, useEffect } from 'react'
import {
  TextField,
  Button,
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  ListSubheader,
  InputAdornment
} from '@mui/material'
import Autocomplete, { createFilterOptions, autocompleteClasses } from '@mui/material/Autocomplete'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { getRestaurantDetail } from '../../apollo'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import PeopleOutlineRoundedIcon from '@mui/icons-material/PeopleOutlineRounded'
import { CATERING, cateringTitles, isCateringItem } from '../../utils/catering'
import { CREATE_ADVANCED_MENU_ITEM, UPDATE_ADVANCED_MENU_ITEM, GENERATE_ITEM_AI_CONTENT, CLEAR_ITEM_AI_CONTENT, CREATE_ADVANCED_MODIFIER } from '../../apollo/advancedMenuMutations'
import { GET_ADVANCED_MENU_ITEMS, GET_ADVANCED_MODIFIER_GROUPS, GET_RESTAURANT_ITEMS, GET_ADVANCED_OPTION_GROUPS, GET_ADVANCED_MODIFIERS } from '../../apollo/advancedMenuQueries'
import { ATTACH_OPTION_GROUP_TO_ITEM, DETACH_OPTION_GROUP_FROM_ITEM } from '../../apollo/advancedMenuMutations'
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineRounded'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DynamicPricingTable from '../Pricing/DynamicPricingTable'
import DietaryTagChips from './DietaryTagChips'
import CategoryMultiSelect, { uniqueCategories } from './CategoryMultiSelect'
import PointTabPlaceholder from './PointTabPlaceholder'

// Use the same env-driven URL as the rest of the app so this works against
// the Railway backend in development and prod alike.
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8001'

import { mw, tabsSx, chipSx, aiBtnSx, pillBtnSx, pillBtnPrimarySx, iconBtnDangerSx, formSectionTitleSx } from '../../views/menuWorkspaceStyles'
import AlternateNameField from './AlternateNameField'

const filterIngredients = createFilterOptions()

// The restaurant's categories (the same query the category picker runs), for
// which of them are catering.
const GET_RESTAURANT_CATEGORIES = gql`${getRestaurantDetail}`

// Sticky header row (title + primary action) at the top of the form.
const stickyHeaderSx = {
  position: 'sticky', top: 0, zIndex: 5, bgcolor: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
  py: 1.5, mx: -3.5, px: 3.5, mb: 1.5, borderBottom: `1px solid ${mw.color.divider}`,
  '& .MuiTypography-h5': { fontSize: 15, fontWeight: 700, color: mw.color.textSecondary },
}

// A grey "$" box at the start of a price field.
const dollarPrefix = (
  <InputAdornment
    position="start"
    sx={{
      alignSelf: 'stretch', height: 'auto', maxHeight: 'none', m: 0, mr: 1.5, px: 1.5,
      bgcolor: mw.color.subtleBg, borderRight: `1px solid ${mw.color.border}`,
      borderRadius: `${mw.radius.sm} 0 0 ${mw.radius.sm}`,
      '& .MuiTypography-root': { color: mw.color.textSecondary, fontWeight: 700, fontSize: 14.5 },
    }}
  >
    $
  </InputAdornment>
)

// Section spacing inside the Basic tab.
const sectionGap = { mt: 3.5 }

const ItemForm = ({ item, menuId, menuName, defaultCategoryName, onSuccess, onCancel, onDirtyChange, menuLanguages }) => {
  const { t } = useTranslation()
  const [currentTab, setCurrentTab] = useState(0)

  // The restaurant's chosen second/third menu language. AlternateNameField uses
  // these to label itself and to pick the right script for phonetic typing and
  // AI corrections; it falls back to generic labels when nothing is set.
  const lang1Code = menuLanguages?.language1 || null
  const lang2Code = menuLanguages?.language2 || null

  // Basic fields
  const [name, setName] = useState('')
  const [alternateName1, setAlternateName1] = useState('')
  const [alternateName2, setAlternateName2] = useState('')
  const [description, setDescription] = useState('')
  const [basePrice, setBasePrice] = useState('')
  const [variantPrices, setVariantPrices] = useState({})
  const [taxOption, setTaxOption] = useState('DEFAULT')
  const [dietaryTags, setDietaryTags] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [allowOnlineOrders, setAllowOnlineOrders] = useState(true)
  const [modifierGroupIds, setModifierGroupIds] = useState([])
  const [optionGroupIds, setOptionGroupIds] = useState([])
  // Critical ingredients come from two libraries: modifiers and other menu
  // items. Kept as separate id lists because the two tables number their rows
  // independently — a modifier and an item can share the same _id.
  const [ingredientIds, setIngredientIds] = useState([])
  const [ingredientItemIds, setIngredientItemIds] = useState([])
  const [imageUrl, setImageUrl] = useState('')
  const [imageUploading, setImageUploading] = useState(false)
  // Cached AI fallbacks shown (with a Remove option) when there's no real value.
  const [aiImageUrl, setAiImageUrl] = useState('')
  const [aiDescription, setAiDescription] = useState('')
  // The catering version of this (regular) item, or null.
  const [cateringItemId, setCateringItemId] = useState(null)
  // How many people one serves (migration 135): one number, or one per size
  // when the item is priced by size. Kept as text while typing.
  const [serves, setServes] = useState('')
  const [variantServes, setVariantServes] = useState({})

  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // New items must pick a category before the rest of the form unlocks.
  const [categoryStepDone, setCategoryStepDone] = useState(false)

  // Unsaved-changes tracking, reported up so the parent can guard Close/backdrop.
  // A ref suppresses the flag while we programmatically populate from `item`.
  const [dirty, setDirty] = useState(false)
  const suppressDirtyRef = React.useRef(false)

  const restaurantId = localStorage.getItem('restaurantId')

  // Query modifier groups for attachment
  const { data: modifierGroupsData } = useQuery(GET_ADVANCED_MODIFIER_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  // Query option groups for attachment
  const { data: optionGroupsData } = useQuery(GET_ADVANCED_OPTION_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  // Query modifiers for ingredients
  const { data: modifiersData } = useQuery(GET_ADVANCED_MODIFIERS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  // Query the restaurant's items — an item can be an ingredient of another item
  // (prepped components like "Meatballs" are sold on their own and used inside
  // other dishes). Usually already in the cache from the Items tab.
  const { data: restaurantItemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId
  })

  // Alphabetical lists for the pickers below.
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  const allModifierGroups = (modifierGroupsData?.advancedModifierGroups || []).slice().sort(byName)
  const allOptionGroups = (optionGroupsData?.advancedOptionGroups || []).slice().sort(byName)
  const allModifiers = (modifiersData?.advancedModifiers || []).slice().sort(byName)
  // An item can never be its own ingredient — it would 86 itself.
  const allIngredientItems = (restaurantItemsData?.advancedRestaurantItems || [])
    .filter(i => !item || String(i._id) !== String(item._id))
    .slice().sort(byName)

  // One picker, two sources. The composite key keeps a modifier and an item
  // that happen to share an _id apart.
  const ingredientOptions = [
    ...allModifiers.map(m => ({ _id: m._id, name: m.name, kind: 'MODIFIER' })),
    ...allIngredientItems.map(i => ({ _id: i._id, name: i.name, kind: 'ITEM' })),
  ]
  // Catering version picker: only for a regular item, and only once the
  // restaurant has a catering category. The choices are the items in catering
  // categories; the current link stays listed even if its category changed.
  const { data: categoriesData } = useQuery(GET_RESTAURANT_CATEGORIES, {
    variables: { id: restaurantId },
    skip: !restaurantId
  })
  const cateringTitleSet = cateringTitles(categoriesData?.restaurant?.categories)
  const showCateringPicker = cateringTitleSet.size > 0 &&
    !isCateringItem({ categoryName: selectedCategories.map(c => c.title).join(',') }, cateringTitleSet)
  const cateringOptions = (restaurantItemsData?.advancedRestaurantItems || [])
    .filter(i => (!item || String(i._id) !== String(item._id)) &&
      (isCateringItem(i, cateringTitleSet) || String(i._id) === String(cateringItemId)))
    .slice().sort(byName)
  const selectedCateringItem = cateringOptions.find(i => String(i._id) === String(cateringItemId)) || null

  const ingredientKey = (opt) => `${opt.kind}:${opt._id}`
  const selectedIngredients = ingredientOptions.filter(opt => (
    opt.kind === 'MODIFIER'
      ? ingredientIds.some(id => String(id) === String(opt._id))
      : ingredientItemIds.some(id => String(id) === String(opt._id))
  ))
  // Inline-create a missing ingredient from the search box, so the user need
  // not leave for the Modifiers tab. It becomes a normal library modifier
  // (same defaults as the Modifiers tab) and is ticked on this item.
  const [createIngredient, { loading: creatingIngredient }] = useMutation(CREATE_ADVANCED_MODIFIER, {
    // The picker derives its chips from the library, so wait for the refetch
    // before selecting the new id or its chip won't render.
    refetchQueries: [{ query: GET_ADVANCED_MODIFIERS, variables: { restaurantId } }],
    awaitRefetchQueries: true,
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const handleIngredientsChange = async (newValue) => {
    const createOpt = newValue.find(o => o.kind === 'CREATE')
    const picked = newValue.filter(o => o.kind !== 'CREATE')
    setIngredientIds(picked.filter(o => o.kind === 'MODIFIER').map(o => o._id))
    setIngredientItemIds(picked.filter(o => o.kind === 'ITEM').map(o => o._id))
    if (!createOpt) return

    const { data: created } = await createIngredient({
      variables: { restaurantId, input: { name: createOpt.name, modifierType: 'OTHER', isIngredient: true, isModifier: true } }
    })
    const mod = created?.createAdvancedModifier
    if (mod) setIngredientIds(ids => [...ids, mod._id])
  }

  useEffect(() => {
    // Suppress dirty-tracking while we programmatically fill the form.
    suppressDirtyRef.current = true
    if (item) {
      setName(item.name || '')
      setAlternateName1(item.alternateName1 || '')
      setAlternateName2(item.alternateName2 || '')
      setDescription(item.description || '')
      setBasePrice(item.basePrice != null ? item.basePrice : '')
      const vp = item.variantPrices
      setVariantPrices(typeof vp === 'string' ? ((() => { try { return JSON.parse(vp) } catch(e) { return {} } })()) : (vp || {}))
      setTaxOption(item.taxOption || 'DEFAULT')
      setDietaryTags(item.dietaryTags || [])
      setAllowOnlineOrders(item.isActive !== false)
      setModifierGroupIds(item.modifierGroups?.map(mg => mg._id) || item.modifierGroupIds || [])
      setImageUrl(item.imageUrl || '')
      setAiImageUrl(item.aiImageUrl || '')
      setAiDescription(item.aiDescription || '')
      setOptionGroupIds(item.optionGroups?.map(og => og._id) || item.optionGroupIds || [])
      setIngredientIds(item.criticalIngredients?.map(i => i._id) || [])
      setIngredientItemIds(item.criticalIngredientItems?.map(i => i._id) || [])
      setCateringItemId(item.cateringItemId || null)
      setServes(item.serves != null ? String(item.serves) : '')
      const vs = item.variantServes
      setVariantServes(typeof vs === 'string' ? ((() => { try { return JSON.parse(vs) || {} } catch (e) { return {} } })()) : (vs || {}))
      // Parse categoryName back into selectedCategories
      if (item.categoryName) {
        // De-duplicated so an item already saved as "CLASSICS, CLASSICS" shows
        // one chip and is repaired on its next save.
        const catNames = item.categoryName.split(',').map(s => s.trim()).filter(Boolean)
        setSelectedCategories(uniqueCategories(catNames.map(title => ({ _id: title, title }))))
      } else {
        setSelectedCategories([])
      }
    } else if (defaultCategoryName) {
      // New item opened while a category filter is active — pre-select it.
      setSelectedCategories([{ _id: defaultCategoryName, title: defaultCategoryName }])
    }
    // After this batch of programmatic setState flushes (and the dirty-watcher
    // effect runs with suppression on), re-enable tracking and reset to clean.
    const id = setTimeout(() => {
      suppressDirtyRef.current = false
      setDirty(false)
    }, 0)
    return () => clearTimeout(id)
  }, [item, defaultCategoryName])

  // Report dirty state up so the parent can guard Close/backdrop.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  // Any change to an editable value marks the form dirty — unless mid-load.
  useEffect(() => {
    if (suppressDirtyRef.current) return
    setDirty(true)
  }, [
    name, alternateName1, alternateName2, description, basePrice, variantPrices,
    taxOption, dietaryTags, selectedCategories, allowOnlineOrders, modifierGroupIds,
    optionGroupIds, ingredientIds, ingredientItemIds, imageUrl, cateringItemId,
    serves, variantServes
  ])

  const refetchQueries = [
    { query: GET_RESTAURANT_ITEMS, variables: { restaurantId } },
    ...(menuId ? [{ query: GET_ADVANCED_MENU_ITEMS, variables: { menuId } }] : [])
  ]

  const [createItem, { loading: creating }] = useMutation(CREATE_ADVANCED_MENU_ITEM, {
    refetchQueries,
    onCompleted: () => {
      // Save and close right away. The success message only stays visible
      // where the form remains mounted (inline in the Menu Builder).
      // Clear the parent's dirty flag synchronously — the dirty-report effect
      // fires too late for the onSuccess close below.
      setSuccess(t('Item created successfully!'))
      setDirty(false)
      onDirtyChange && onDirtyChange(false)
      resetForm()
      onSuccess && onSuccess(t('Item created successfully!'))
      setTimeout(() => setSuccess(''), 2000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  const [updateItem, { loading: updating }] = useMutation(UPDATE_ADVANCED_MENU_ITEM, {
    refetchQueries,
    onCompleted: () => {
      setDirty(false)
      onDirtyChange && onDirtyChange(false)
      onSuccess && onSuccess(t('Item updated successfully!'))
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    }
  })

  // On-demand AI generation. Stores the result in the AI-preview state (shown
  // with an "AI generated" label + Remove), so it's distinct from a real upload.
  const [generateAI, { loading: generatingAI }] = useMutation(GENERATE_ITEM_AI_CONTENT, {
    onError: (err) => { setError(err.message); setTimeout(() => setError(''), 5000) }
  })
  const [clearAI, { loading: clearingAI }] = useMutation(CLEAR_ITEM_AI_CONTENT, {
    onError: (err) => { setError(err.message); setTimeout(() => setError(''), 5000) }
  })
  const [genTarget, setGenTarget] = useState(null) // 'image' | 'description' | null

  const handleGenerateAI = async (target) => {
    if (!item?._id) return
    setGenTarget(target)
    try {
      const { data: res } = await generateAI({
        variables: {
          id: item._id,
          image: target === 'image',
          description: target === 'description',
        },
      })
      const r = res?.generateItemAIContent
      if (!r) return
      if (target === 'image' && r.aiImageUrl) setAiImageUrl(r.aiImageUrl)
      if (target === 'description' && r.aiDescription) setAiDescription(r.aiDescription)
    } finally {
      setGenTarget(null)
    }
  }

  // Remove a cached AI value (persists immediately so it won't reappear).
  const handleRemoveAI = async (target) => {
    if (!item?._id) return
    setGenTarget(target)
    try {
      await clearAI({
        variables: { id: item._id, image: target === 'image', description: target === 'description' },
      })
      if (target === 'image') setAiImageUrl('')
      if (target === 'description') setAiDescription('')
    } finally {
      setGenTarget(null)
    }
  }

  const resetForm = () => {
    // Clearing the form is not a user edit — keep it from marking dirty.
    suppressDirtyRef.current = true
    setName('')
    setAlternateName1('')
    setAlternateName2('')
    setDescription('')
    setBasePrice('')
    setVariantPrices({})
    setTaxOption('DEFAULT')
    setDietaryTags([])
    setSelectedCategories([])
    setAllowOnlineOrders(true)
    setModifierGroupIds([])
    setOptionGroupIds([])
    setIngredientIds([])
    setIngredientItemIds([])
    setImageUrl('')
    setAiImageUrl('')
    setAiDescription('')
    setCateringItemId(null)
    setCurrentTab(0)
    setCategoryStepDone(false)
    setTimeout(() => {
      suppressDirtyRef.current = false
      setDirty(false)
    }, 0)
  }

  // Auto-derive pricing mode from variant prices
  const hasVariants = variantPrices && Object.keys(variantPrices).length > 0 &&
    Object.entries(variantPrices).some(([k, v]) => k.trim() !== '')

  // Serves (migration 135). Sizes come from the multipricing table; a number
  // for a size that was removed is dropped on save.
  const sizeNames = hasVariants ? Object.keys(variantPrices).filter(k => k.trim() !== '') : []
  const servesLoaded = !item || Object.prototype.hasOwnProperty.call(item, 'serves')
  const wholeOrNull = (v) => {
    const n = parseInt(v, 10)
    return Number.isInteger(n) && n > 0 ? n : null
  }
  // The item's own number always (shown on its card, "Serves 10"); per size
  // as well when the item is priced by size.
  const servesInput = () => {
    if (!hasVariants) return { serves: wholeOrNull(serves), variantServes: null }
    const perSize = {}
    sizeNames.forEach(size => {
      const n = wholeOrNull(variantServes[size])
      if (n) perSize[size] = n
    })
    return { serves: wholeOrNull(serves), variantServes: Object.keys(perSize).length ? perSize : null }
  }
  const servesField = (label, value, onChange, key) => (
    <TextField
      key={key}
      fullWidth
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      inputProps={{ min: 1, max: 1000, step: 1, inputMode: 'numeric' }}
      InputProps={{ endAdornment: <InputAdornment position="end">{t('people')}</InputAdornment> }}
      placeholder="10"
      sx={{ '& .MuiOutlinedInput-root': { fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
    />
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError(t('Item name is required'))
      return
    }

    // New items always start from the category step — keep that guarantee
    // even if the user removed the category chips afterwards.
    if (!item && selectedCategories.length === 0) {
      setError(t('Please choose a category for this item'))
      return
    }

    const derivedPricingMode = hasVariants ? 'VARIANTS' : 'SINGLE'

    if (derivedPricingMode === 'SINGLE' && !basePrice && basePrice !== 0) {
      setError(t('Base price is required when no variant prices are set'))
      return
    }

    // Convert selectedCategories to comma-separated string for backward compat
    const categoryName = uniqueCategories(selectedCategories).map(c => c.title).join(', ')

    const input = {
      ...(menuId ? { menuId } : {}),
      name: name.trim(),
      alternateName1: alternateName1.trim() || null,
      alternateName2: alternateName2.trim() || null,
      description: description.trim(),
      categoryName,
      pricingMode: derivedPricingMode,
      basePrice: parseFloat(basePrice) || null,
      variantPrices: hasVariants ? variantPrices : null,
      taxOption,
      prepTimeMinutes: null,
      calories: null,
      imageUrl: imageUrl || null,
      dietaryTags: dietaryTags.length > 0 ? dietaryTags : null,
      allergenTags: null,
      modifierGroupIds: modifierGroupIds.length > 0 ? modifierGroupIds : null,
      optionGroupIds: optionGroupIds.length > 0 ? optionGroupIds : null,
      criticalIngredientIds: ingredientIds.length > 0 ? ingredientIds : null,
      criticalIngredientItemIds: ingredientItemIds.length > 0 ? ingredientItemIds : null,
      isActive: allowOnlineOrders,
      displayOrder: 0,
      // Only when the picker was on screen: a catering item (or a restaurant
      // with no catering yet) leaves the saved link alone.
      ...(showCateringPicker ? { cateringItemId: cateringItemId || null } : {}),
      // Only when this item arrived with its serves, so a save from a screen
      // that never loaded them can't blank them.
      ...(servesLoaded ? servesInput() : {})
    }

    if (item) {
      updateItem({
        variables: { id: item._id, input }
      })
    } else {
      createItem({
        variables: { restaurantId, input }
      })
    }
  }

  const loading = creating || updating

  // Step 1 for new items: pick (or create) the category first. The rest of
  // the form only shows after Continue, with the category already attached.
  if (!item && !categoryStepDone) {
    return (
      <Box>
        <Box sx={stickyHeaderSx}>
          <Typography variant="h5" sx={{ color: mw.color.textPrimary, fontWeight: 'bold' }}>
            {t('Create Item')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ ...pillBtnPrimarySx, minWidth: 100, minHeight: 40 }}
            disabled={selectedCategories.length === 0}
            onClick={() => setCategoryStepDone(true)}
          >
            {t('Continue')}
          </Button>
        </Box>
        <Typography variant="caption" display="block" sx={{ mb: 2, color: mw.color.textMuted }}>
          {t('Step 1 of 2 — Category')}
        </Typography>
        <Typography variant="subtitle2" gutterBottom sx={{ color: mw.color.textSecondary }}>
          {t('Which category does this item belong to?')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: mw.color.textMuted }}>
          {t('Pick from your categories, or type a new name to create one right here.')}
        </Typography>
        <CategoryMultiSelect
          selectedCategories={selectedCategories}
          onChange={setSelectedCategories}
          restaurantId={restaurantId}
        />
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Header area: Title + Save — sticky so it stays visible while the form
          scrolls inside the dialog (no scrolling back up to save). */}
      <Box sx={stickyHeaderSx}>
        <Typography variant="h5" sx={{ color: mw.color.textPrimary, fontWeight: 'bold' }}>
          {item ? t('Edit Item') : t('Create Item')}
        </Typography>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          size="small"
          sx={{ ...pillBtnPrimarySx, minWidth: 100, minHeight: 40 }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : item ? (
            t('Save')
          ) : (
            t('Create')
          )}
        </Button>
      </Box>

      {menuName ? (
        <Typography variant="caption" display="block" sx={{ mb: 1, color: mw.color.textMuted }}>
          {t('Adding to menu')}: {menuName}
        </Typography>
      ) : (
        <Typography variant="caption" display="block" sx={{ mb: 1, color: mw.color.textMuted }}>
          {t('Creating standalone item — add to menus later')}
        </Typography>
      )}

      <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ ...tabsSx, mb: 2.5, borderBottom: `1px solid ${mw.color.border}` }}
        textColor="primary" indicatorColor="primary"
      >
        <Tab label={t('Basic')} sx={{ color: mw.color.textMuted }} />
        <Tab label={t('POS')} sx={{ color: mw.color.textMuted }} />
      </Tabs>

      {/* Floating feedback — fixed to the screen so it's visible no matter
          how far down the form the user has scrolled. */}
      <Snackbar open={!!error} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" sx={{ boxShadow: mw.shadow.pop, borderRadius: mw.radius.sm }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" sx={{ boxShadow: mw.shadow.pop, borderRadius: mw.radius.sm }}>{success}</Alert>
      </Snackbar>

      {/* BASIC TAB */}
      {currentTab === 0 && (
        <Box>
          {/* NAMES — name and alternate names on the left, the image on the right */}
          <Typography sx={formSectionTitleSx}>{t('Names')}</Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 220px' },
            gridTemplateAreas: { xs: '"name" "image" "alt"', md: '"name image" "alt image"' },
            gridTemplateRows: { md: 'auto 1fr' },
            columnGap: 3,
            rowGap: 1.5,
            alignItems: 'start',
          }}>
          {/* Name - full width */}
          <TextField
            fullWidth
            label={t('Item Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            size="small"
            placeholder={t('e.g., Margherita Pizza, Classic Burger')}
            sx={{ gridArea: 'name', mt: 0.75, mb: 0 }}
          />

          {/* Image Upload */}
          <Box sx={{ gridArea: 'image', mt: 0, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: mw.color.textSecondary, mb: 1, fontSize: 13, fontWeight: 700 }}>
              {t('Item Image')}
            </Typography>
            {imageUrl ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Box
                  component="img"
                  src={imageUrl}
                  alt={name || 'Item'}
                  sx={{
                    width: 220,
                    maxWidth: '100%',
                    height: 150,
                    objectFit: 'cover',
                    borderRadius: mw.radius.panel,
                    border: `1px solid ${mw.color.border}`,
                    display: 'block'
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setImageUrl('')}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: '#fff',
                    border: `1px solid ${mw.color.border}`,
                    boxShadow: '0 2px 6px rgba(23,24,30,0.12)',
                    borderRadius: '50%',
                    color: mw.color.textMuted,
                    '&:hover': { bgcolor: mw.color.dangerSoft, color: mw.color.danger }
                  }}
                >
                  <DeleteIcon fontSize="small" sx={{ color: 'inherit' }} />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={imageUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                disabled={imageUploading}
                size="small"
                sx={{
                  width: 220,
                  maxWidth: '100%',
                  height: 150,
                  flexDirection: 'column',
                  gap: 0.5,
                  borderRadius: mw.radius.panel,
                  border: `1.5px dashed ${mw.color.borderStrong}`,
                  borderStyle: 'dashed',
                  borderColor: mw.color.borderStrong,
                  bgcolor: mw.color.headBg,
                  color: mw.color.textSecondary,
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  '& .MuiButton-startIcon': { m: 0, '& > *:nth-of-type(1)': { fontSize: 26, color: mw.color.accent } },
                  '&:hover': { borderColor: mw.color.accent, bgcolor: mw.color.accentSoft }
                }}
              >
                {imageUploading ? t('Uploading...') : t('Upload Image')}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    setImageUploading(true)
                    const formData = new FormData()
                    formData.append('image', file)
                    formData.append('folder', 'rushmyfood/items')
                    fetch(`${SERVER_URL}/upload/image`, {
                      method: 'POST',
                      body: formData
                    })
                      .then(res => res.json())
                      .then(data => {
                        if (data.url) {
                          setImageUrl(data.url)
                        } else {
                          setError(data.error || t('Upload failed'))
                        }
                      })
                      .catch(err => {
                        setError(t('Upload failed: ') + err.message)
                      })
                      .finally(() => {
                        setImageUploading(false)
                      })
                    e.target.value = ''
                  }}
                />
              </Button>
            )}
            {/* AI image: shown (with badge + Remove) when there's no real image
                but a cached AI one exists. Customers see this until a real upload. */}
            {item?._id && !imageUrl && aiImageUrl && (
              <Box sx={{ position: 'relative', display: 'inline-block', mt: 1 }}>
                <Box
                  component="img"
                  src={aiImageUrl}
                  alt={name || 'Item'}
                  sx={{ width: 220, maxWidth: '100%', height: 150, objectFit: 'cover', borderRadius: mw.radius.panel, border: `2px dashed ${mw.color.aiBorder}`, display: 'block' }}
                />
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, px: 1, height: 22, display: 'inline-flex', alignItems: 'center', borderRadius: mw.radius.pill, bgcolor: mw.color.ai, color: '#fff', fontSize: 11, fontWeight: 700 }}>
                  {t('AI generated')}
                </Box>
                <Tooltip title={t('Remove AI image')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveAI('image')}
                      disabled={clearingAI && genTarget === 'image'}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: `1px solid ${mw.color.border}`, boxShadow: '0 2px 6px rgba(23,24,30,0.12)', borderRadius: '50%', color: mw.color.textMuted, '&:hover': { bgcolor: mw.color.dangerSoft, color: mw.color.danger } }}
                    >
                      <DeleteIcon fontSize="small" sx={{ color: 'inherit' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
            {/* Generate an AI image (existing items only, when none exists yet). */}
            {item?._id && !imageUrl && !aiImageUrl && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleGenerateAI('image')}
                disabled={generatingAI}
                startIcon={generatingAI && genTarget === 'image' ? <CircularProgress size={12} /> : null}
                sx={{ ...aiBtnSx, display: 'flex', width: 220, maxWidth: '100%', mt: 1, fontSize: 13, px: 1.5 }}
              >
                {generatingAI && genTarget === 'image' ? t('Generating...') : '✨ ' + t('Generate AI image')}
              </Button>
            )}
          </Box>

          {/* Alternate Names. Generation lives in Menus > Menu Languages (which
              translates the whole menu at once); each field here carries its own
              "Fix with AI" for correcting a single name. */}
          <Box sx={{ gridArea: 'alt', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="subtitle2" sx={{ color: mw.color.textSecondary, flex: 1, fontSize: 13, fontWeight: 700 }}>
              {t('Alternate Names')}
            </Typography>
          </Box>
          <AlternateNameField
            value={alternateName1}
            onChange={setAlternateName1}
            languageCode={lang1Code}
            fallbackLabel={t('Alternate Name #1')}
            fallbackPlaceholder={t('e.g. Spanish or French name')}
            serverUrl={SERVER_URL}
          />
          <AlternateNameField
            value={alternateName2}
            onChange={setAlternateName2}
            languageCode={lang2Code}
            fallbackLabel={t('Alternate Name #2')}
            fallbackPlaceholder={t('Another language variant')}
            serverUrl={SERVER_URL}
          />
          </Box>
          </Box>

          {/* PRICING — price + tax, then the multipricing card */}
          <Typography sx={{ ...formSectionTitleSx, ...sectionGap }}>{t('Pricing')}</Typography>
          {/* Price + Tax: two-column */}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('Price ($)')}
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                size="small"
                inputProps={{ step: 0.01, min: 0 }}
                InputProps={{ startAdornment: dollarPrefix }}
                placeholder="0.00"
                helperText={t('Can be $0 if using variant prices')}
                FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
                sx={{ '& .MuiOutlinedInput-root': { pl: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('Tax')}</InputLabel>
                <Select
                  value={taxOption}
                  onChange={(e) => setTaxOption(e.target.value)}
                  label={t('Tax')}
                >
                  <MenuItem value="DEFAULT">{t('Default')}</MenuItem>
                  <MenuItem value="TAX_EXEMPT">{t('Exempt')}</MenuItem>
                  <MenuItem value="OTHER">{t('Other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Multipricing in its own soft card (styles reach into the shared
              DynamicPricingTable from here, so other users of it are untouched). */}
          <Box sx={{
            '& > .MuiBox-root': { mt: 2 },
            '& .MuiTypography-subtitle2': { fontSize: 13, fontWeight: 700, color: mw.color.textSecondary, mb: 1 },
            '& .MuiPaper-root': { p: 2, bgcolor: mw.color.headBg, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel, boxShadow: 'none' },
            '& .MuiPaper-root .MuiButton-root': { color: mw.color.accent, fontWeight: 600 },
            '& .MuiPaper-root > .MuiBox-root:last-of-type': { bgcolor: mw.color.cardBg, border: `1px dashed ${mw.color.border}`, borderRadius: mw.radius.xs, '& .MuiTypography-root': { color: mw.color.textMuted } },
          }}>
            <DynamicPricingTable
              variantPrices={variantPrices}
              onChange={setVariantPrices}
              label={t('Multipricing (Sizes/Variants)')}
            />
          </Box>

          {/* SERVES — people per order, per size when priced by size (migration
              135). Customers see it; catering suggests how many to order. */}
          {servesLoaded && (
            <Box sx={{ mt: 2, p: 2, bgcolor: mw.color.headBg, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.panel }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PeopleOutlineRoundedIcon sx={{ fontSize: 18, color: mw.color.textSecondary }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: mw.color.textSecondary }}>{t('Serves')}</Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: mw.color.textMuted, mt: 0.5, mb: 1.5, lineHeight: 1.45 }}>
                {t('How many people one order feeds, shown on the item as "Serves 10". Catering suggests how many to order for the guests from it.')}
              </Typography>
              <Box sx={{ maxWidth: { sm: 240 } }}>
                {servesField(t('Serves'), serves, setServes, 'single')}
              </Box>
              {hasVariants && (
                <>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: mw.color.textSecondary, mt: 2, mb: 1 }}>
                    {t('Each size (optional)')}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {sizeNames.map(size => (
                      <Grid item xs={12} sm={6} key={size}>
                        {servesField(size, variantServes[size] ?? '', (v) => setVariantServes(prev => ({ ...prev, [size]: v })), size)}
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          )}

          {/* DESCRIPTION */}
          <Typography sx={{ ...formSectionTitleSx, ...sectionGap }}>{t('Description')}</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              {/* Generate button only when there's no real AND no AI description. */}
              {item?._id && !description && !aiDescription && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleGenerateAI('description')}
                    disabled={generatingAI}
                    startIcon={generatingAI && genTarget === 'description' ? <CircularProgress size={12} /> : null}
                    sx={{ ...aiBtnSx, fontSize: 13, px: 1.5 }}
                  >
                    {generatingAI && genTarget === 'description' ? t('Generating...') : '✨ ' + t('Generate AI description')}
                  </Button>
                </Box>
              )}
              <TextField
                fullWidth
                label={t('Description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={5}
                size="small"
                placeholder={t('Describe your item')}
              />
              {/* AI description: shown (with badge + Remove) when there's no real
                  description but a cached AI one exists. Customers see this. */}
              {item?._id && !description && aiDescription && (
                <Box sx={{ mt: 1.5, p: 2, borderRadius: mw.radius.panel, border: `1px solid ${mw.color.aiBorder}`, bgcolor: mw.color.aiSoft }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.1, height: 24, borderRadius: mw.radius.pill, bgcolor: mw.color.ai, color: '#fff', fontSize: 11.5, fontWeight: 700 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 13, color: '#fff' }} />
                      {t('AI generated')}
                    </Box>
                    <Tooltip title={t('Remove AI description')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAI('description')}
                          disabled={clearingAI && genTarget === 'description'}
                          sx={iconBtnDangerSx}
                        >
                          <DeleteIcon fontSize="small" sx={{ color: 'inherit' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" sx={{ color: mw.color.textSecondary, lineHeight: 1.55 }}>{aiDescription}</Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* ICONS / BADGES, then ORGANIZE (categories, ordering, groups, ingredients) */}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ ...formSectionTitleSx, ...sectionGap }}>
                {t('Icons / Badges')}
              </Typography>
              <DietaryTagChips
                selectedTags={dietaryTags}
                onChange={setDietaryTags}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...formSectionTitleSx, mt: 2 }}>{t('Organize')}</Typography>
              <Typography variant="subtitle2" gutterBottom sx={{ color: mw.color.textSecondary, fontSize: 13, fontWeight: 700 }}>
                {t('Categories')}
              </Typography>
              <CategoryMultiSelect
                selectedCategories={selectedCategories}
                onChange={setSelectedCategories}
                restaurantId={restaurantId}
              />
            </Grid>
            {showCateringPicker && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: mw.color.textSecondary, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <RoomServiceRoundedIcon aria-hidden sx={{ fontSize: 17, color: CATERING.accent }} />
                  {t('Catering version')}
                </Typography>
                <Autocomplete
                  options={cateringOptions}
                  value={selectedCateringItem}
                  onChange={(e, opt) => setCateringItemId(opt ? opt._id : null)}
                  getOptionLabel={(opt) => opt.name || ''}
                  isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      <ListItemText
                        primary={option.name}
                        secondary={option.categoryName}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { color: mw.color.textMuted } }}
                      />
                    </li>
                  )}
                  noOptionsText={t('No items in your catering categories yet')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t('Choose a catering item')}
                      helperText={t('Customers see an "Available for catering" button under this item that opens the catering version.')}
                      FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>

          {/* Allow Online Orders + Modifier Groups: two-column */}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowOnlineOrders}
                    onChange={(e) => setAllowOnlineOrders(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('Allow Online Orders')}
                sx={{ m: 0, width: '100%', minHeight: 44, pl: 0.25, pr: 1.5, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.sm, bgcolor: mw.color.cardBg, '& .MuiFormControlLabel-label': { fontWeight: 600, color: mw.color.textPrimary } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('Modifier Groups')}</InputLabel>
                <Select
                  multiple
                  value={modifierGroupIds}
                  onChange={(e) => setModifierGroupIds(e.target.value)}
                  input={<OutlinedInput label={t('Modifier Groups')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const group = modifierGroupsData?.advancedModifierGroups?.find(g => g._id === value)
                        return <Chip key={value} label={group?.name || value} size="small" sx={chipSx.modifier} />
                      })}
                    </Box>
                  )}
                >
                  {allModifierGroups.map((group) => (
                    <MenuItem key={group._id} value={group._id}>
                      <Checkbox checked={modifierGroupIds.indexOf(group._id) > -1} />
                      <ListItemText
                        primary={group.name}
                        secondary={group.isTemplate ? t('Template') : `${group.modifiers?.length || 0} modifiers`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Option Groups */}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('Option Groups')}</InputLabel>
              <Select
                multiple
                value={optionGroupIds}
                onChange={(e) => setOptionGroupIds(e.target.value)}
                input={<OutlinedInput label={t('Option Groups')} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const group = optionGroupsData?.advancedOptionGroups?.find(g => g._id === value)
                      return <Chip key={value} label={group?.name || value} size="small" sx={chipSx.option} />
                    })}
                  </Box>
                )}
              >
                {allOptionGroups.map((group) => (
                  <MenuItem key={group._id} value={group._id}>
                    <Checkbox checked={optionGroupIds.indexOf(group._id) > -1} />
                    <ListItemText
                      primary={group.name}
                      secondary={group.choices?.join(', ')}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Ingredients */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: mw.color.textSecondary, fontSize: 13, fontWeight: 700 }}>
              {t('Ingredients')}
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={ingredientOptions}
              groupBy={(opt) => (opt.kind === 'CREATE' ? 'CREATE' : opt.kind === 'ITEM' ? t('Items') : t('Modifiers'))}
              getOptionLabel={(opt) => opt.name}
              // Typed name that matches nothing exactly → offer to create it
              // (last row, like the Categories picker).
              filterOptions={(options, params) => {
                const filtered = filterIngredients(options, params)
                const input = params.inputValue.trim()
                if (input && !options.some(o => o.name.trim().toLowerCase() === input.toLowerCase())) {
                  filtered.push({ _id: input, name: input, kind: 'CREATE' })
                }
                return filtered
              }}
              // Same as MUI's default group render, minus the header for the
              // create row.
              renderGroup={(params) => (
                <li key={params.key}>
                  {params.group !== 'CREATE' && (
                    <ListSubheader component="div" sx={{ top: -8, bgcolor: 'background.paper' }}>
                      {params.group}
                    </ListSubheader>
                  )}
                  <Box component="ul" sx={{ p: 0, [`& .${autocompleteClasses.option}`]: { pl: 3 } }}>
                    {params.children}
                  </Box>
                </li>
              )}
              value={selectedIngredients}
              onChange={(e, newValue) => handleIngredientsChange(newValue)}
              loading={creatingIngredient}
              disabled={creatingIngredient}
              isOptionEqualToValue={(option, value) => ingredientKey(option) === ingredientKey(value)}
              renderOption={(props, option, { selected }) => (
                option.kind === 'CREATE' ? (
                  <li {...props} key={ingredientKey(option)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddCircleOutlineIcon fontSize="small" sx={{ color: mw.color.emerald }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.emerald }}>
                          {t('Create "{{name}}"', { name: option.name })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                          {t('New ingredient — will also appear on the Modifiers tab')}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                ) : (
                  <li {...props} key={ingredientKey(option)}>
                    <Checkbox size="small" checked={selected} disableRipple sx={{ mr: 1, p: 0.5 }} />
                    <ListItemText primary={option.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </li>
                )
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={ingredientKey(option)}
                    label={option.name}
                    size="small"
                    sx={{ ...chipSx.ingredient, '& .MuiChip-deleteIcon': { color: mw.color.aiDark } }}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={t('Search ingredients...')}
                  helperText={t('Select modifiers or items that are base ingredients of this item')}
                  FormHelperTextProps={{ sx: { color: mw.color.textMuted } }}
                />
              )}
            />
          </Box>

          {/* Cancel button for edit mode */}
          {item && onCancel && (
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${mw.color.divider}` }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={loading}
                size="small"
                sx={{ ...pillBtnSx, minHeight: 40 }}
              >
                {t('Cancel')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* POINT TAB */}
      {currentTab === 1 && (
        <PointTabPlaceholder />
      )}
    </Box>
  )
}

export default ItemForm
